<template>
  <base-slide-over ref="slideover" @hiding="reset()" title="Add Activity" size="lg">
    <base-form ref="form" v-model="isFormValid" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6">
      <base-select
        required
        label="Activity Type"
        :options="todoItemTypes"
        v-model="selectedItemTypeId"
        placeholder="Select activity type"
        label-key="label"
        value-key="todoItemTypeId"
      />
      <div class="tw-mb-4">
        <label :class="['tw-text-xs tw-font-normal', `tw-text-gray-900 tw-ml-3`]">Member</label>
        <member-search v-if="selectedMember === null" :businessId="businessId" @memberSelected="selectMember"></member-search>
        <div v-else class="tw-text-sm tw-font-light tw-text-gray-700 tw-w-full tw-flex">
          <span class="tw-my-auto tw-ml-3">{{ selectedMember.fullName }}</span
          ><x-icon class="tw-h-5 tw-text-red-600 tw-cursor-pointer tw-ml-3" aria-label="Dismiss" @click="selectedMember = null" />
        </div>
      </div>
      <base-select
        label="Prescription (optional)"
        :disabled="isPrescriptionsLoading === true"
        :options="prescriptions"
        v-model="selectedPrescriptionId"
        placeholder="Select a prescription"
        label-key="name"
        value-key="id"
        class="tw-my-5"
      />
      <base-select
        :label="capitalize(this.organization.customerLabelSingular) + ' plan (optional)'"
        :disabled="isPlansLoading === true"
        :options="plans"
        v-model="selectedPlanId"
        placeholder="Select a plan"
        label-key="planName"
        value-key="userBusinessPlanId"
        class="tw-my-5"
      />
      <base-date-picker class="tw-mt-8" v-model="dueDate" :label="'Due Date'" :inline="true" required />
      <base-textarea v-if="isNoteRequired" class="tw-mt-8" v-model="note" label="Note" />
    </base-form>
    <template #fixed-bottom>
      <inline-error class="tw-my-3" id="add-business-modal-error" :title="errorTitle" :message="errorMessage" :error="error" />
      <div class="sm:tw-flex sm:tw-flex-row-reverse tw-p-3">
        <base-button @click="save" :loading="isLoading" class="tw-w-full sm:tw-w-auto sm:tw-ml-3" :disabled="!isFormValid">Save</base-button>
        <base-button @click="hide" type="flat" class="tw-w-full sm:tw-w-auto tw-mt-2 sm:tw-mt-0">
          Cancel
        </base-button>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import MemberSearch from "@/components/MemberSearch";
import InlineError from "@/components/InlineError";
import XIcon from "@/assets/svgs/x-icon.svg";
import { mapGetters } from "vuex";
import { capitalize } from "lodash";

export default {
  name: "NewTodoItem",
  components: {
    InlineError,
    MemberSearch,
    XIcon,
  },
  props: {
    businessId: String,
    userId: String,
  },
  data() {
    return {
      isLoading: false,
      isFormValid: true,
      selectedItemTypeId: null,
      name: null,
      errorTitle: "",
      errorMessage: "",
      error: null,
      todoItemTypes: [],
      selectedMember: null,
      dueDate: null,
      prescriptions: [],
      isPrescriptionsLoading: false,
      selectedPrescriptionId: null,
      plans: [],
      isPlansLoading: false,
      selectedPlanId: null,
      note: null,
    };
  },
  created() {
    this.loadTodoTypes();
    if (this.userId) this.loadUser();
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    computedSelectedType() {
      return this.todoItemTypes.find((i) => {
        return i.todoItemTypeId === this.selectedItemTypeId;
      });
    },
    isNoteRequired() {
      return (
        this.computedSelectedType && (this.computedSelectedType.typeConstant === "PRESCRIBER_CLARIFICATION" || this.computedSelectedType.typeConstant === "ONLINE_SUPPORT_QUESTION")
      );
    },
  },
  methods: {
    capitalize,
    loadTodoTypes() {
      this.isLoading = true;
      this.$axios
        .get(`/businesses/${this.businessId}/todo/types`)
        .then((res) => {
          this.todoItemTypes = res.data.sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    loadUser() {
      this.$axios.get(`/businesses/${this.businessId}/members/${this.userId}`).then((res) => {
        this.selectedMember = res.data;
        this.loadPrescriptions();
        this.loadPlans();
      });
    },
    loadPrescriptions() {
      this.isPrescriptionsLoading = true;
      this.$axios
        .get(`/businesses/${this.businessId}/members/${this.selectedMember.userId}/prescriptions`)
        .then((res) => {
          this.prescriptions = this.formatRxResults(res.data);
        })
        .finally(() => {
          this.isPrescriptionsLoading = false;
        });
    },
    formatRxResults(items) {
      var formattedResults = [];
      for (const item of items) {
        item.name = item.businessProgram.name;
        formattedResults.push(item);
      }
      return formattedResults;
    },
    loadPlans() {
      this.isPlansLoading = true;
      this.$axios
        .get(`/businesses/${this.businessId}/members/${this.selectedMember.userId}/memberplans`)
        .then((res) => {
          this.plans = res.data;
        })
        .finally(() => {
          this.isPlansLoading = false;
        });
    },
    selectMember(member) {
      this.selectedMember = member;
      this.loadPrescriptions();
      this.loadPlans();
    },
    reset() {
      this.dueDate = null;
      this.selectedItemTypeId = null;
      this.errorTitle = "";
      this.errorMessage = "";
      this.error = null;
      this.prescriptions = [];
      this.plans = [];
      this.selectedPrescriptionId = null;
      this.selectedPlanId = null;

      if (this.userId === null) this.selectedMember = null;
      this.$refs.form.reset();
    },
    show() {
      this.$refs.slideover.show();
    },
    hide() {
      this.$refs.slideover.hide();
      this.reset();
    },
    save() {
      this.errorTitle = "";
      this.errorMessage = "";
      if (!this.$refs.form.validate()) return;

      if (this.selectedMember === null) {
        this.errorTitle = `Please select a ${this.organization.customerLabelSingular}`;
        this.errorMessage = `Please select a ${this.organization.customerLabelSingular} to assign this activity to.`;
        return;
      }

      if (this.selectedPrescriptionId === null && this.selectedPlanId === null) {
        this.errorTitle = `Please select a prescription or a plan`;
        this.errorMessage = `Activities must be associated with either a prescription or a plan. Please choose one.`;
        return;
      }

      if (
        this.computedSelectedType &&
        (this.computedSelectedType.typeConstant === "PRESCRIBER_CLARIFICATION" || this.computedSelectedType.typeConstant === "ONLINE_SUPPORT_QUESTION") &&
        this.note === null
      ) {
        this.errorTitle = `Please enter a note to the provider`;
        this.errorMessage = `This activity type requires a note to the provider. Please enter a note.`;
        return;
      }

      this.isLoading = true;

      this.$axios
        .post(`/businesses/${this.businessId}/todo`, {
          todoItemTypeId: this.computedSelectedType.todoItemTypeId,
          userId: this.selectedMember.userId,
          dueDate: this.dueDate.toLocaleString(),
          businessPrescriptionId: this.selectedPrescriptionId,
          userBusinessPlanId: this.selectedPlanId,
          note: this.note,
        })
        .then(() => {
          this.EventBus.$emit("activitiesUpdated", {});
          this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Activity Saved", message: `Your activity was saved.` });
          this.hide();
        })
        .catch((err) => {
          this.errorTitle = `Error creating activity`;
          this.errorMessage = "Sorry, we had trouble creating this activity. The technicians have been notified. Please try again.";
          this.error = err;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>
